<script setup lang="ts">
import { ref } from 'vue'
import Modal from '../Modal/index.vue'
import { deleteAPIKey } from '~~/src/api/api-key'
import type { APIKey } from '~~/src/api/api-key'

defineProps<{
  apiKeys: APIKey[]
  isLoading: boolean
  error: Error | null
  toggleTokenVisibility: (keyId: number) => void
}>()

const emit = defineEmits(['updateKeys'])

const toast = useToast()
const user = useUser()

const copyToClipboard = (token: string) => {
  navigator.clipboard.writeText(token).then(() => {
    toast.success('Secret Key Copied')
  }).catch(() => {
    toast.error('Failed to copy Secret Key')
  })
}

const mapPermissions = (permissions: number[]): string[] => {
  const permissionMap: Record<number, string> = {
    1: 'All',
    2: 'Read',
    3: 'Write',
  }
  return permissions.map(permission => permissionMap[permission] || 'unknown')
}

const isModalVisible = ref(false)
const selectedKeyId = ref<number | undefined>(undefined)

const confirmDelete = (id: number) => {
  selectedKeyId.value = id
  isModalVisible.value = true
}

const handleUpdateKeys = () => {
  emit('updateKeys')
}

const handleCloseModal = () => {
  isModalVisible.value = false
  selectedKeyId.value = undefined
}
</script>

<template>
  <div class="p-4">
    <div v-if="isLoading" class="text-center py-4">
      Loading...
    </div>
    <div v-if="error" class="text-center py-4 text-red-500">
      {{ error.message }}
    </div>
    <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div v-for="key in apiKeys" :key="key.id" class="bg-white p-4 rounded-lg shadow-md border">
        <div class="mb-2">
          <span class="font-bold">Name:</span> {{ key.name }}
        </div>
        <div class="mb-2">
          <span class="font-bold">Created By:</span> {{ user?.name }}
        </div>
        <div class="mb-2">
          <span class="font-bold">Secret Key:</span>
          <div class="flex items-center">
            <span class="truncate">{{ key.token }}</span>
          </div>
        </div>
        <div class="mb-2">
          <span class="font-bold">Permissions:</span> {{ mapPermissions(key.permissions).join(', ') }}
        </div>
        <div class="mb-2">
          <span class="font-bold">Created At:</span> {{ formatUSDate(new Date(key.created_at)) }}
        </div>
        <div class="flex space-x-2">
          <button class="color-dark-gray hover:color-darker-gray underline mr-1.5" @click="copyToClipboard(key.token)">
            <div i-mdi:content-copy class="m-1.5" />
          </button>
          <button class="color-dark-gray hover:color-darker-gray underline" @click="confirmDelete(key.id)">
            <div i-mdi:delete class="m-1.5" />
          </button>
        </div>
      </div>
    </div>

    <Modal
      :is-visible="isModalVisible"
      :selected-key-id="selectedKeyId"
      title="Confirm Deletion"
      message="Are you sure you want to delete this API Key?"
      :delete-function="deleteAPIKey"
      @update-keys="handleUpdateKeys"
      @close-modal="handleCloseModal"
    />
  </div>
</template>

<style scoped>
.fixed {
  position: fixed;
  z-index: 9999;
}
</style>
